import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type BootstrapDTO, type CreatedInviteDTO, type InviteDTO, type MeDTO, type Role, THEMES, type ThemeId, type UserDTO } from "@justone/shared";
import { errorMessage, fieldError, http } from "../api/http";
import { keys, useBootstrap, useChannelAction, useChannels, useInvites, useMarkNotifications, useMe, useNotifications, useThreads, useUserMap, useUsers } from "../api/queries";
import { disablePush, enablePush, pushState, type PushState } from "../api/push";
import { formatDay, formatRemaining, formatTime } from "../lib/format";
import { describeNotification, notificationPath } from "../lib/notice";
import { AvatarCropper } from "../components/AvatarCropper";
import { useDefaultReminder, useQuietHours, useReminderAction } from "../api/tasks";
import { Avatar, Dialog, Field, Icon, EmptyState, Skeleton, useConfirm, useToast } from "../components/ui";
import { inNativeApp } from "../lib/native";
import { applyTheme, THEME_LOOK } from "../lib/theme";
import type { WorkspaceOutlet } from "./Workspace";

export function PageShell({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  const { openNav } = useOutletContext<WorkspaceOutlet>();
  return (
    <>
      <header className="topbar">
        <button type="button" className="icon-btn menu-toggle" onClick={openNav} aria-label="채널 목록 열기">
          <Icon name="menu" />
        </button>
        <h1>{title}</h1>
        {actions && <div className="actions">{actions}</div>}
      </header>
      <div className="page">
        <div className="page-inner">{children}</div>
      </div>
    </>
  );
}

/* ───────────── 채널 둘러보기 ───────────── */

export function BrowseChannelsPage() {
  const { data: channels = [] } = useChannels();
  const { openCreate } = useOutletContext<WorkspaceOutlet>();
  const action = useChannelAction();
  const toast = useToast();
  const active = channels.filter((c) => !c.archived);
  const archived = channels.filter((c) => c.archived);

  const list = (items: typeof channels) => (
    <div className="list">
      {items.map((c) => (
        <div className="list-row" key={c.id}>
          <div className="grow">
            <Link to={`/c/${c.id}`} className="title" style={{ color: "inherit", textDecoration: "none" }}>
              #{c.name}
            </Link>
            <div className="sub">
              {c.memberCount}명 참여{c.topic ? `, ${c.topic}` : ""}
            </div>
          </div>
          {c.joined ? (
            <span className="pill pill-ok">참여 중</span>
          ) : (
            !c.archived && (
              <button type="button" className="btn btn-sm" onClick={() => action.mutate({ id: c.id, action: "join" }, { onError: (e) => toast(errorMessage(e)) })}>
                참여하기
              </button>
            )
          )}
        </div>
      ))}
    </div>
  );

  return (
    <PageShell
      title="채널 둘러보기"
      actions={
        <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
          채널 만들기
        </button>
      }
    >
      {active.length ? list(active) : <p className="empty">아직 채널이 없습니다. 첫 채널을 만들어 보세요.</p>}
      {archived.length > 0 && (
        <>
          <h2>보관된 채널</h2>
          {list(archived)}
        </>
      )}
    </PageShell>
  );
}

/* ───────────── 스레드 모아보기 ───────────── */

/** 마크다운 기호를 대충 걷어낸 한 줄 미리보기 */
const snippet = (body: string) => body.replace(/[*_~`>#[\]]/g, "").replace(/\s+/g, " ").slice(0, 120);

export function ThreadsPage() {
  const threads = useThreads();
  const { data: channels = [] } = useChannels();
  const users = useUserMap();
  const channelName = (id: number | null) => channels.find((c) => c.id === id)?.name ?? "알 수 없는 채널";
  return (
    <PageShell title="스레드">
      {threads.isPending ? (
        <Skeleton kind="list" rows={5} />
      ) : !threads.data?.length ? (
        <EmptyState icon="threads" title="따라가는 스레드가 없습니다">
          메시지에 답글을 달거나 멘션되면 그 스레드가 여기에 모입니다.
        </EmptyState>
      ) : (
        <div className="list">
          {threads.data.map(({ root, unread, lastReply }) => (
            <Link key={root.id} to={`${root.channelId !== null ? `/c/${root.channelId}` : `/d/${root.conversationId}`}/t/${root.id}`} className={`list-row thread-row${unread ? " unread" : ""}`}>
              <Avatar user={root.authorId ? users.get(root.authorId) : undefined} size={32} />
              <div className="grow">
                <div className="sub">
                  {root.channelId !== null ? `#${channelName(root.channelId)}` : "DM"}, {root.authorId ? users.get(root.authorId)?.displayName : ""}
                </div>
                <div className="title">{root.deleted ? "삭제된 메시지" : snippet(root.body)}</div>
                {lastReply && (
                  <div className="sub">
                    {users.get(lastReply.authorId ?? 0)?.displayName}: {lastReply.deleted ? "삭제된 메시지" : snippet(lastReply.body)} ({formatTime(lastReply.createdAt)})
                  </div>
                )}
              </div>
              {unread > 0 ? <span className="badge">{unread}</span> : <span className="pill">답글 {root.replyCount}</span>}
            </Link>
          ))}
        </div>
      )}
    </PageShell>
  );
}

/* ───────────── 알림 센터 ───────────── */

export function NotificationsPage() {
  const q = useNotifications();
  const mark = useMarkNotifications();
  const users = useUserMap();
  const { data: channels = [] } = useChannels();
  const navigate = useNavigate();
  const channelName = (id: number | null) => channels.find((c) => c.id === id)?.name ?? "알 수 없는 채널";
  const list = q.data?.notifications ?? [];

  const open = (n: (typeof list)[number]) => {
    if (!n.read) mark.mutate({ ids: [n.id] });
    const path = notificationPath(n);
    if (path) navigate(path);
  };

  return (
    <PageShell
      title="알림"
      actions={
        q.data && q.data.unread > 0 ? (
          <button type="button" className="btn btn-sm" onClick={() => mark.mutate({ all: true })}>
            모두 읽음
          </button>
        ) : undefined
      }
    >
      {q.isPending ? (
        <Skeleton kind="list" rows={5} />
      ) : list.length === 0 ? (
        <EmptyState icon="bell" title="알림이 없습니다">
          누가 나를 멘션하거나, 따라가는 스레드에 답글이 달리거나, 할일을 맡기면 여기에 모입니다.
        </EmptyState>
      ) : (
        <div className="list">
          {list.map((n) => {
            const actor = n.actorId ? users.get(n.actorId) : undefined;
            const place = n.channelId !== null ? `#${channelName(n.channelId)}` : "DM";
            return (
              <div key={n.id} className={`list-row notice${n.read ? "" : " unread"}`}>
                <button type="button" className="notice-open" onClick={() => open(n)}>
                  {n.type === "task_reminder" ? (
                    <span className="notice-icon" aria-hidden="true">
                      <Icon name="bell" />
                    </span>
                  ) : (
                    <Avatar user={actor} size={32} />
                  )}
                  <div className="grow">
                    <div>{describeNotification(n, actor?.displayName ?? "알 수 없는 사용자", place)}</div>
                    {n.preview && <div className="sub preview">{n.preview}</div>}
                  </div>
                  <time className="sub" dateTime={n.createdAt}>
                    {formatDay(n.createdAt)} {formatTime(n.createdAt)}
                  </time>
                </button>
                {n.type === "task_reminder" && n.taskId !== null && !n.read && <ReminderActions taskId={n.taskId} />}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}

/* ───────────── 이 기기 알림 ───────────── */

/** 리마인더 알림에서 바로: 완료 / 1시간 후 / 내일 */
function ReminderActions({ taskId }: { taskId: number }) {
  const act = useReminderAction();
  const toast = useToast();
  const run = (action: "complete" | "snooze" | "tomorrow", done: string) => act.mutate({ taskId, action }, { onSuccess: () => toast(done), onError: (e) => toast(errorMessage(e)) });
  return (
    <div className="notice-actions">
      <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => run("complete", "완료했습니다.")}>
        완료
      </button>
      <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => run("snooze", "1시간 뒤에 다시 알려드립니다.")}>
        1시간 후
      </button>
      <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => run("tomorrow", "내일로 미뤘습니다.")}>
        내일
      </button>
    </div>
  );
}

const DEFAULT_REMINDER_OPTIONS: [number | null, string][] = [
  [null, "두지 않음"],
  [0, "마감 시각에"],
  [10, "10분 전"],
  [30, "30분 전"],
  [60, "1시간 전"],
];

function DefaultReminder({ me }: { me: MeDTO }) {
  const set = useDefaultReminder();
  const toast = useToast();
  return (
    <Field id="default-reminder" label="마감 시각이 있는 할일의 기본 리마인더" hint="내가 맡은 할일에 마감 시각을 정하면 이 리마인더를 하나 둡니다. 할일마다 바꿀 수 있습니다.">
      <select
        id="default-reminder"
        className="input"
        style={{ maxWidth: 240 }}
        value={me.defaultReminder === null ? "" : String(me.defaultReminder)}
        onChange={(e) => set.mutate(e.target.value === "" ? null : Number(e.target.value), { onSuccess: () => toast("기본 리마인더를 바꿨습니다."), onError: (err) => toast(errorMessage(err)) })}
      >
        {DEFAULT_REMINDER_OPTIONS.map(([v, label]) => (
          <option key={label} value={v === null ? "" : String(v)}>
            {label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** 방해 금지 시간: 이 시간에는 푸시를 보내지 않고 알림 센터에만 쌓는다 */
function QuietHours({ me }: { me: MeDTO }) {
  const set = useQuietHours();
  const toast = useToast();
  const [on, setOn] = useState(!!me.quietHours);
  const [start, setStart] = useState(me.quietHours?.start ?? "22:00");
  const [end, setEnd] = useState(me.quietHours?.end ?? "08:00");
  const save = (next: { start: string; end: string } | null) =>
    set.mutate(next, { onSuccess: () => toast(next ? `방해 금지: ${next.start}~${next.end}` : "방해 금지를 껐습니다."), onError: (e) => toast(errorMessage(e)) });
  return (
    <div className="quiet-hours">
      <label className="qa-option switch-label">
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={on}
          onChange={(e) => {
            setOn(e.target.checked);
            save(e.target.checked ? { start, end } : null);
          }}
        />
        방해 금지 시간
      </label>
      <span className="quiet-range">
        <label className="sr-only" htmlFor="quiet-start">
          시작
        </label>
        <input id="quiet-start" type="time" className="input input-sm" value={start} disabled={!on} onChange={(e) => setStart(e.target.value)} />
        ~
        <label className="sr-only" htmlFor="quiet-end">
          끝
        </label>
        <input id="quiet-end" type="time" className="input input-sm" value={end} disabled={!on} onChange={(e) => setEnd(e.target.value)} />
        {on && (start !== me.quietHours?.start || end !== me.quietHours?.end) && (
          <button type="button" className="btn btn-sm" onClick={() => save({ start, end })} disabled={!start || !end || set.isPending}>
            저장
          </button>
        )}
      </span>
      <p className="muted">이 시간에는 푸시를 보내지 않고 알림 센터에만 쌓아 둡니다. 할일마다 "방해 금지 시간에도 알림"을 켜면 그 리마인더는 보냅니다.</p>
    </div>
  );
}

function DevicePush() {
  const boot = useBootstrap().data;
  const key = boot?.pushPublicKey ?? null;
  // JustOne 앱은 Apple 푸시(APNs)로 받는다
  const app = inNativeApp();
  const available = app ? !!boot?.appPush : !!key;
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    pushState().then(setState, () => setState("unsupported"));
  }, []);

  const run = async (fn: () => Promise<PushState>) => {
    setBusy(true);
    try {
      setState(await fn());
    } catch (e) {
      toast(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!available) return <p className="empty">{app ? "이 서버에는 앱 알림이 아직 설정되지 않았습니다." : "이 서버에는 푸시 알림이 설정되지 않았습니다."}</p>;
  if (state === null) return null;
  if (state === "needs-install")
    return (
      <p>
        iPhone·iPad에서는 Safari의 공유 버튼 → <strong>홈 화면에 추가</strong>로 JustOne을 설치한 뒤, 설치한 앱에서 알림을 켤 수 있습니다.
        {boot?.iosApp && (
          <>
            {" "}
            아래 내 메뉴의 <strong>iPhone·iPad 앱 설치</strong>로 앱을 설치해도 됩니다.
          </>
        )}
      </p>
    );
  if (state === "unsupported") return <p className="empty">이 브라우저는 푸시 알림을 지원하지 않습니다.</p>;
  if (state === "denied")
    return app ? (
      <p>이 기기에서 JustOne 알림이 꺼져 있습니다. 설정 앱 → 알림 → JustOne에서 알림을 허용한 뒤 다시 시도하세요.</p>
    ) : (
      <p>이 브라우저에서 알림이 차단되어 있습니다. 브라우저 설정에서 one.kastalien.org의 알림을 허용한 뒤 다시 시도하세요.</p>
    );
  return state === "on" ? (
    <div className="inline-form">
      <span className="pill pill-ok">이 기기에서 알림을 받습니다</span>
      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => http.post("/api/push/test").then(() => toast("시험 알림을 보냈습니다. 앱을 보고 있으면 알림은 뜨지 않습니다."), (e) => toast(errorMessage(e)))}>
        시험 알림 보내기
      </button>
      <button type="button" className="btn btn-sm btn-quiet btn-danger" disabled={busy} onClick={() => run(disablePush)}>
        이 기기 알림 끄기
      </button>
    </div>
  ) : (
    <button type="button" className="btn" disabled={busy} onClick={() => run(() => enablePush(key))}>
      이 기기에서 알림 켜기
    </button>
  );
}

/* ───────────── 멤버와 초대 (관리자) ───────────── */

const STATUS: Record<InviteDTO["status"], string> = { pending: "대기 중", used: "사용함", expired: "만료됨", revoked: "취소됨" };

export function MembersPage() {
  const me = useMe();
  if (me.role !== "admin") return <PageShell title="멤버와 초대"><p className="empty">관리자만 볼 수 있습니다.</p></PageShell>;
  return (
    <PageShell title="멤버와 초대">
      <InviteSection />
      <MemberSection me={me} />
    </PageShell>
  );
}

function InviteSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const google = useBootstrap().data?.googleEnabled ?? false;
  const invites = useInvites();
  const [role, setRole] = useState<Role>("member");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<CreatedInviteDTO | null>(null);
  const [err, setErr] = useState<unknown>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const inv = await http.post<CreatedInviteDTO>("/api/invites", { role, ...(email ? { email } : {}) });
      setCreated(inv);
      setEmail("");
      await qc.invalidateQueries({ queryKey: keys.invites });
    } catch (e) {
      setErr(e);
    }
  };

  const copy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.url).then(
      () => toast("초대 링크를 복사했습니다."),
      () => toast("복사하지 못했습니다. 링크를 직접 선택해 복사하세요."),
    );
  };

  const revoke = async (id: number) => {
    try {
      await http.delete(`/api/invites/${id}`);
      await qc.invalidateQueries({ queryKey: keys.invites });
    } catch (e) {
      toast(errorMessage(e));
    }
  };

  return (
    <section aria-labelledby="invite-heading">
      <h2 id="invite-heading">초대하기</h2>
      <form className="inline-form" onSubmit={create}>
        <Field id="invite-role" label="역할">
          <select id="invite-role" className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="member">멤버</option>
            <option value="admin">관리자</option>
          </select>
        </Field>
        {google && (
          <Field id="invite-email" label="Google 이메일 (선택)" error={fieldError(err, "email")}>
            <input id="invite-email" type="email" className="input" placeholder="지정하면 그 계정만 가입" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </Field>
        )}
        <button type="submit" className="btn btn-primary">
          초대 링크 만들기
        </button>
      </form>
      {err && !fieldError(err, "email") ? <p className="form-error" style={{ marginTop: 12 }}>{errorMessage(err)}</p> : null}
      {created && (
        <div className="invite-link">
          <code>{created.url}</code>
          <button type="button" className="btn btn-sm" onClick={copy}>
            복사
          </button>
          <span className="sub" style={{ width: "100%", fontSize: 13 }}>
            이 링크는 지금만 볼 수 있습니다. 7일 안에 한 번 쓸 수 있습니다.
          </span>
        </div>
      )}
      {invites.data && invites.data.length > 0 && (
        <div className="list" style={{ marginTop: 16 }}>
          {invites.data.map((i) => (
            <div className="list-row" key={i.id}>
              <div className="grow">
                <div>
                  {i.email ?? "링크 초대"} <span className="pill">{i.role === "admin" ? "관리자" : "멤버"}</span>
                </div>
                <div className="sub">
                  {i.createdBy}님이 만듦{i.status === "pending" ? `, ${formatRemaining(i.expiresAt)}` : ""}
                  {i.usedBy ? `, ${i.usedBy}님이 가입` : ""}
                </div>
              </div>
              <span className={`pill${i.status === "used" ? " pill-ok" : ""}`}>{STATUS[i.status]}</span>
              {i.status === "pending" && (
                <button type="button" className="btn btn-sm btn-danger" onClick={() => revoke(i.id)}>
                  취소
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function MemberSection({ me }: { me: MeDTO }) {
  const { data: users = [] } = useUsers();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const update = async (u: UserDTO, patch: { role?: Role; active?: boolean }) => {
    if (patch.active === false && !(await confirm({ title: `${u.displayName}님 비활성화`, body: "바로 로그아웃되고 다시 로그인할 수 없습니다. 쓴 메시지와 할일은 그대로 남고, 언제든 다시 활성화할 수 있습니다.", confirmLabel: "비활성화", danger: true }))) return;
    try {
      await http.patch(`/api/users/${u.id}`, patch);
      await qc.invalidateQueries({ queryKey: keys.users });
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  return (
    <section aria-labelledby="members-heading">
      <h2 id="members-heading">멤버 {users.filter((u) => u.active).length}명</h2>
      <div className="list">
        {users.map((u) => (
          <div className="list-row" key={u.id} style={{ opacity: u.active ? 1 : 0.6 }}>
            <Avatar user={u} size={36} />
            <div className="grow">
              <div className="title">
                {u.displayName} {u.id === me.id && <span className="pill">나</span>} {!u.active && <span className="pill">비활성</span>}
              </div>
              <div className="sub">
                {u.username ? `@${u.username}` : "Google 계정"}, {u.online ? "접속 중" : "오프라인"}
              </div>
            </div>
            <label className="sr-only" htmlFor={`role-${u.id}`}>
              {u.displayName} 역할
            </label>
            <select id={`role-${u.id}`} className="input" style={{ width: "auto", minHeight: 32 }} value={u.role} disabled={!u.active} onChange={(e) => update(u, { role: e.target.value as Role })}>
              <option value="member">멤버</option>
              <option value="admin">관리자</option>
            </select>
            {u.id !== me.id && (
              <button type="button" className={`btn btn-sm${u.active ? " btn-danger" : ""}`} onClick={() => update(u, { active: !u.active })}>
                {u.active ? "비활성화" : "다시 활성화"}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────── 내 계정 ───────────── */

function ProfilePhoto({ me }: { me: MeDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const done = async (message: string) => {
    await Promise.all([qc.invalidateQueries({ queryKey: keys.bootstrap }), qc.invalidateQueries({ queryKey: keys.users })]);
    toast(message);
  };

  const save = async (blob: Blob) => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", blob, "avatar.webp");
      await http.upload("/api/me/avatar", form);
      setFile(null);
      await done("프로필 사진을 바꿨습니다.");
    } catch (e) {
      toast(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await http.delete("/api/me/avatar");
      await done("프로필 사진을 지웠습니다.");
    } catch (e) {
      toast(errorMessage(e));
    }
  };

  return (
    <div className="profile-photo">
      <Avatar user={me} size={72} />
      <label className="btn" htmlFor="avatar-file">
        {me.avatarUrl ? "사진 바꾸기" : "사진 올리기"}
      </label>
      <input
        id="avatar-file"
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) setFile(f);
        }}
      />
      {me.avatarUrl && (
        <button type="button" className="btn btn-quiet btn-danger" onClick={remove}>
          지우기
        </button>
      )}
      <Dialog open={!!file} onClose={() => setFile(null)} label="프로필 사진 자르기">
        <div style={{ padding: "22px 22px 18px" }}>
          <h2>사진 자르기</h2>
          {file && <AvatarCropper file={file} saving={saving} onCancel={() => setFile(null)} onSave={save} />}
        </div>
      </Dialog>
    </div>
  );
}

/** 화면 테마 고르기: 누르면 바로 바뀌고 계정에 저장된다 */
function ThemePicker({ me }: { me: MeDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState<ThemeId>(me.preferences.theme);
  const pick = async (id: ThemeId) => {
    setValue(id);
    applyTheme(id);
    try {
      const next = await http.put<MeDTO>("/api/me/preferences", { theme: id });
      qc.setQueryData<BootstrapDTO>(keys.bootstrap, (b) => (b ? { ...b, me: next } : b));
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  return (
    <fieldset className="theme-picker">
      <legend className="sr-only">화면 테마</legend>
      {THEMES.map((t) => {
        const look = THEME_LOOK[t.id];
        return (
          <label key={t.id} className="theme-card">
            <input type="radio" name="theme" value={t.id} checked={value === t.id} onChange={() => void pick(t.id)} />
            <span className="theme-swatch" aria-hidden="true" style={t.id === "system" ? { background: `linear-gradient(135deg, ${look.paper} 50%, ${THEME_LOOK["pine-dark"].paper} 50%)` } : undefined}>
              <span className="ts-side" style={{ background: look.side }}>
                <i style={{ background: look.sideText, width: "80%", opacity: 0.8 }} />
                <i style={{ background: look.sideText, width: "60%", opacity: 0.5 }} />
                <i style={{ background: look.accent, width: "70%" }} />
              </span>
              <span className="ts-main" style={t.id === "system" ? undefined : { background: look.paper }}>
                <i style={{ background: look.text, width: "80%", opacity: 0.7 }} />
                <i style={{ background: look.muted, width: "60%" }} />
                <i style={{ background: look.accent, width: "36%", height: 8 }} />
              </span>
            </span>
            <span className="theme-name">{t.name}</span>
            <span className="theme-note">{t.note}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

export function SettingsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  // 앱 안에서는 Google 연결을 숨긴다 (Google이 앱 속 웹 화면의 로그인을 막는다)
  const google = (useBootstrap().data?.googleEnabled ?? false) && !inNativeApp();
  const [params] = useSearchParams();
  const [name, setName] = useState(me.displayName);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [pwErr, setPwErr] = useState<unknown>(null);
  const linkError = params.get("error") === "google_in_use" ? "이 Google 계정은 이미 다른 사용자에게 연결되어 있습니다." : null;

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await http.patch("/api/me", { displayName: name });
      await qc.invalidateQueries({ queryKey: keys.bootstrap });
      toast("이름을 바꿨습니다.");
    } catch (e) {
      toast(errorMessage(e));
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwErr(null);
    try {
      await http.post("/api/me/password", { currentPassword: current || undefined, newPassword: next });
      setCurrent("");
      setNext("");
      await qc.invalidateQueries({ queryKey: keys.bootstrap });
      toast("비밀번호를 바꿨습니다.");
    } catch (e) {
      setPwErr(e);
    }
  };

  const unlinkGoogle = async () => {
    try {
      await http.delete("/api/me/google");
      await qc.invalidateQueries({ queryKey: keys.bootstrap });
      toast("Google 연결을 끊었습니다.");
    } catch (e) {
      toast(errorMessage(e));
    }
  };

  const logoutOthers = async () => {
    const { count } = await http.post<{ count: number }>("/api/auth/logout-others");
    toast(count ? `다른 기기 ${count}곳에서 로그아웃했습니다.` : "로그인된 다른 기기가 없습니다.");
  };

  return (
    <PageShell title="내 계정">
      <h2 id="theme">화면 테마</h2>
      <p style={{ color: "var(--ink-2)", marginTop: 0 }}>나에게만 적용되고, 로그인한 모든 기기에서 같습니다.</p>
      <ThemePicker me={me} />

      <h2>프로필 사진</h2>
      <ProfilePhoto me={me} />

      <h2>이름</h2>
      <form className="inline-form" onSubmit={saveName}>
        <Field id="settings-name" label="다른 사람에게 보이는 이름">
          <input id="settings-name" className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <button type="submit" className="btn">
          저장
        </button>
      </form>

      {me.username && (
        <>
          <h2>{me.hasPassword ? "비밀번호 바꾸기" : "비밀번호 만들기"}</h2>
          <form onSubmit={savePassword} action="/api/me/password" method="post" name="change-password" id="change-password-form" style={{ maxWidth: 380 }}>
            {/* 비밀번호 매니저가 어느 계정의 비밀번호인지 알 수 있도록 */}
            <input type="text" name="username" autoComplete="username" value={me.username} readOnly hidden />
            {pwErr && !fieldError(pwErr, "currentPassword") && !fieldError(pwErr, "newPassword") ? <p className="form-error">{errorMessage(pwErr)}</p> : null}
            {me.hasPassword && (
              <Field id="current-password" label="현재 비밀번호" error={fieldError(pwErr, "currentPassword")}>
                <input id="current-password" name="current-password" type="password" className="input" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
              </Field>
            )}
            <Field id="new-password" label="새 비밀번호" hint="10자 이상" error={fieldError(pwErr, "newPassword")}>
              <input id="new-password" name="password" type="password" className="input" autoComplete="new-password" minLength={10} required value={next} onChange={(e) => setNext(e.target.value)} />
            </Field>
            <button type="submit" className="btn">
              {me.hasPassword ? "비밀번호 바꾸기" : "비밀번호 만들기"}
            </button>
          </form>
        </>
      )}

      {google && (
        <>
          <h2>Google 계정</h2>
          {linkError && <p className="form-error">{linkError}</p>}
          {me.hasGoogle ? (
            <p>
              Google 계정이 연결되어 있습니다.{" "}
              {me.hasPassword && (
                <button type="button" className="btn btn-sm" onClick={unlinkGoogle}>
                  연결 끊기
                </button>
              )}
            </p>
          ) : (
            <a className="btn" href="/auth/google/start?mode=link">
              Google 계정 연결
            </a>
          )}
        </>
      )}

      <h2>알림</h2>
      <p style={{ color: "var(--ink-2)", marginTop: 0 }}>나를 멘션하거나, 따라가는 스레드에 답글이 달리거나, 할일을 맡거나 리마인더 시각이 되면 알려드립니다. 기기마다 따로 켭니다. 채널별 설정은 채널 메뉴에서 바꿀 수 있습니다.</p>
      <DevicePush />
      <QuietHours me={me} />
      <DefaultReminder me={me} />

      <h2>로그인된 기기</h2>
      <p style={{ color: "var(--ink-2)", marginTop: 0 }}>휴대폰을 잃어버렸거나 공용 컴퓨터에서 로그아웃하지 않았다면 다른 기기의 로그인을 모두 끊으세요.</p>
      <button type="button" className="btn" onClick={() => logoutOthers().catch((e) => toast(errorMessage(e)))}>
        다른 기기에서 모두 로그아웃
      </button>
    </PageShell>
  );
}
